/**
 * @file certificate-status.ts
 * @description Сертификаты производителя в кабинете (phase19.16, партия
 * «Юридические документы», шаг В; SB141; uiux-legal-steps-b-c §6):
 * статус на сайте (шесть состояний §6.1), подсказка о сроке при вводе даты,
 * проверка файла до отправки и тексты ошибок загрузки по коду.
 *
 * Статус считает сервер (`siteStatus` в DTO, одно правило с отбором для
 * сайта); `certificateSiteStatusOf` — запасной расчёт для ответа без поля и
 * подсказка в окне, пока сертификат не сохранён. TODO(shared):
 * `certificateSiteStatus()` в shared — тогда кабинет берёт его оттуда.
 */

import { formatLegalFileSize, LEGAL_PDF_MAX_BYTES } from "@chaiberi/shared";

import type { CertificateSiteStatus, CertificateSiteTone, ProducerCertificate } from "./api-catalog";
import type { StatusTone } from "./shop/status-meta";
import { formatShopDate, getShopTimeZone } from "./shop-time";

/** За сколько дней до конца срока предупреждать (решение 2026-10-19, подсказка интерфейса). */
export const CERT_EXPIRY_WARN_DAYS = 30;

/** Предел файла сертификата — как у PDF документов (20 МБ). */
export const CERT_FILE_MAX_BYTES = LEGAL_PDF_MAX_BYTES;

/** Разрешённые типы файла (§6.3). */
export const CERT_FILE_ACCEPT = "application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png";

const MIME_OK: ReadonlySet<string> = new Set(["application/pdf", "image/jpeg", "image/png"]);
const EXT_OK = /\.(pdf|jpe?g|png)$/i;

/** Тон статуса сервера → тон пилюли кабинета. */
export const CERT_TONE_STATUS: Readonly<Record<CertificateSiteTone, StatusTone>> = {
  neutral: "neutral",
  error: "error",
  warning: "attention",
  success: "success",
};

/** Сегодня по поясу магазина: `2026-10-19`. */
export function shopToday(now: Date = new Date()): string {
  return now.toLocaleDateString("en-CA", { timeZone: getShopTimeZone() });
}

/** День из даты сертификата (`2027-10-12T00:00:00.000Z` → `2027-10-12`). */
const day = (iso: string): string => iso.substring(0, 10);

/** Дней между двумя датами `YYYY-MM-DD`. */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** Есть ли у сертификата файл (новый DTO — `file`, прежний — `fileKey`). */
export const hasCertFile = (c: Pick<ProducerCertificate, "file" | "fileKey">): boolean => (c.file !== undefined ? c.file !== null : c.fileKey !== null);

/** Статус на сайте (§6.1): срабатывает первое правило. */
export function certificateSiteStatusOf(
  c: Pick<ProducerCertificate, "public" | "validUntil" | "file" | "fileKey">,
  producerActive: boolean,
  today: string = shopToday(),
): CertificateSiteStatus {
  if (!producerActive) return { code: "producer_inactive", label: "Не показывается — производитель выключен", tone: "neutral" };
  if (!c.public) return { code: "hidden", label: "Скрыт — на сайте не показывается", tone: "neutral" };
  const until = c.validUntil ? day(c.validUntil) : null;
  if (until !== null && until < today) return { code: "expired", label: "Просрочен — на сайте не показывается", tone: "error" };
  if (!hasCertFile(c)) return { code: "no_file", label: "Только номер — в «Сертификатах» не показывается", tone: "warning" };
  if (until !== null && daysBetween(today, until) <= CERT_EXPIRY_WARN_DAYS) {
    return { code: "expiring", label: `На сайте · истекает ${formatShopDate(`${until}T12:00:00Z`)}`, tone: "warning" };
  }
  return { code: "on_site", label: until === null ? "На сайте · бессрочный" : "На сайте", tone: "success" };
}

/** Статус: с сервера, иначе запасной расчёт. */
export function certificateStatus(c: ProducerCertificate, producerActive: boolean): CertificateSiteStatus {
  return c.siteStatus ?? certificateSiteStatusOf(c, producerActive);
}

/** Подсказка о сроке в окне сертификата при вводе даты (§6.3); null — подсказки нет. */
export function certificateExpiryHint(validUntil: string, today: string = shopToday()): { tone: "error" | "warning"; text: string } | null {
  if (!validUntil) return null;
  const label = formatShopDate(`${validUntil}T12:00:00Z`);
  if (validUntil < today) return { tone: "error", text: `Срок истёк ${label} — на сайте документ не показывается.` };
  if (daysBetween(today, validUntil) <= CERT_EXPIRY_WARN_DAYS) {
    return { tone: "warning", text: `Срок истекает ${label} — после этого документ пропадёт с сайта.` };
  }
  return null;
}

/** Проверка файла до отправки (§6.3): тип и размер; null — файл подходит. */
export function certificateFileIssue(file: Pick<File, "name" | "type" | "size">): string | null {
  if (!MIME_OK.has(file.type) && !EXT_OK.test(file.name)) return "Можно загрузить PDF, JPG или PNG";
  if (file.size > CERT_FILE_MAX_BYTES) return "Файл больше 20 МБ — сожмите или разделите его";
  return null;
}

/**
 * Реестр текстов ошибок загрузки по части кода: сервер шлёт
 * `producer_cert_file_type|_broken|_encrypted|_empty|_too_large|_changed`,
 * `storage_unavailable` (контракт api 2026-10-19).
 */
const UPLOAD_ERRORS: ReadonlyArray<[RegExp, string]> = [
  [/too_large/, "Файл больше 20 МБ — сожмите или разделите его"],
  [/file_empty/, "Файл пустой — выберите другой файл"],
  [/file_changed/, "Файл только что заменил другой сотрудник — закройте окно и откройте сертификат заново"],
  [/encrypted/, "PDF защищён паролем — покупатели не смогут его открыть. Сохраните без пароля"],
  [/broken/, "Файл не открывается — сохраните его заново и загрузите"],
  [/not_pdf|type|mime|kind_mismatch/, "Можно загрузить PDF, JPG или PNG"],
  [/storage_unavailable/, "Хранилище файлов недоступно. Сведения сохранятся, файл добавьте позже"],
  [/network_error/, "Нет связи с сервером — проверьте интернет и повторите"],
];

/** Текст ошибки загрузки файла для человека. */
export function certificateUploadErrorText(code: string, fallback: string): string {
  return UPLOAD_ERRORS.find(([re]) => re.test(code))?.[1] ?? fallback;
}

/** «PDF, 1,2 МБ» — подпись файла в списке. */
export function certificateFileLabel(c: Pick<ProducerCertificate, "file" | "fileKey" | "fileMimeType">): string {
  if (c.file) {
    const kind = c.file.mime === "application/pdf" ? "PDF" : c.file.mime === "image/png" ? "PNG" : "JPG";
    return `${kind}, ${formatLegalFileSize(c.file.sizeBytes)}`;
  }
  return hasCertFile(c) ? "файл есть" : "без файла";
}
